import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// ─── Case conversion helpers ──────────────────────────────────────────────────

function toCamel(str) {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

function toSnake(str) {
  return str.replace(/[A-Z]/g, c => `_${c.toLowerCase()}`);
}

function convertKeys(obj, convert) {
  if (Array.isArray(obj)) return obj.map(item => convertKeys(item, convert));
  if (obj !== null && typeof obj === 'object' && !(obj instanceof File)) {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [convert(k), convertKeys(v, convert)])
    );
  }
  return obj;
}

export function camelizeResponse(data) { return convertKeys(data, toCamel); }
export function snakeifyRequest(data)  { return convertKeys(data, toSnake); }

// ─── Axios instance ───────────────────────────────────────────────────────────

const client = axios.create({ baseURL: BASE_URL });

// Attach JWT token to every request
client.interceptors.request.use(config => {
  const token = localStorage.getItem('sc_access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-refresh JWT on 401, camelize response keys
let isRefreshing = false;
let refreshQueue = [];

client.interceptors.response.use(
  response => {
    response.data = camelizeResponse(response.data);
    return response;
  },
  async error => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(token => {
          original.headers.Authorization = `Bearer ${token}`;
          return client(original);
        });
      }
      isRefreshing = true;
      try {
        const refreshToken = localStorage.getItem('sc_refresh_token');
        if (!refreshToken) throw new Error('No refresh token');
        const { data } = await axios.post(`${BASE_URL}/api/auth/refresh`, { refreshToken });
        const newToken = data.accessToken || data.access_token;
        localStorage.setItem('sc_access_token', newToken);
        refreshQueue.forEach(p => p.resolve(newToken));
        refreshQueue = [];
        original.headers.Authorization = `Bearer ${newToken}`;
        return client(original);
      } catch (refreshErr) {
        refreshQueue.forEach(p => p.reject(refreshErr));
        refreshQueue = [];
        localStorage.removeItem('sc_access_token');
        localStorage.removeItem('sc_refresh_token');
        window.dispatchEvent(new Event('sc:logout'));
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export default client;
